// Feedback #830 (Shawn) — the global ← Back button sent him to the sign-in page.
//
// The button used to be `navigate(-1)` whenever react-router's history index
// was above zero.  A browser will not tell a page what URL sits one entry
// back, so the button had no way to notice that the entry behind the first
// page after sign-in IS the sign-in page.
//
// Two layers fix it:
//   1. Login.js now navigates with { replace: true }, so the sign-in page is
//      replaced by the page it leads to and never stays in history.  That is
//      the real fix.
//   2. This module is the safety net for everything (1) cannot reach — a tab
//      opened before this shipped, an entry pushed by some other public page,
//      a session that started on a bookmark.  Layout records the path of
//      every in-app page it renders, keyed by react-router's history index,
//      in sessionStorage (per-tab, survives a reload, which a ref would not).
//      Back then only walks back when it KNOWS the previous entry is an
//      in-app page; otherwise it goes to a sensible default instead —
//      Calendar, then Matters, then Dashboard, whichever the user can open.
//
// Unknown is treated as unsafe on purpose.  The cost of guessing wrong the
// other way is exactly the reported bug (a signed-in user dropped on the
// sign-in form); the cost of this way is, at worst, one Back click that lands
// on Calendar instead of the page before.

export const NAV_HISTORY_KEY = 'lextrack_nav_hist'
const MAX_ENTRIES = 200

// Pages that must never be a Back destination for a signed-in user.
const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/emergency-lock']

export function isAuthPath (path) {
  if (!path || typeof path !== 'string') return false
  const p = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/'
  return AUTH_PATHS.includes(p)
}

function safeStorage (storage) {
  if (storage) return storage
  try { return window.sessionStorage } catch { return null }
}

export function readNavHistory (storage) {
  const s = safeStorage(storage)
  if (!s) return {}
  try {
    const raw = s.getItem(NAV_HISTORY_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch { return {} }
}

/** Record that history entry `idx` is showing `path`.  Never throws. */
export function recordNavEntry (idx, path, storage) {
  if (!Number.isInteger(idx) || idx < 0 || !path) return
  const s = safeStorage(storage)
  if (!s) return
  try {
    const map = readNavHistory(s)
    map[idx] = path
    // Bound the map: keep the highest MAX_ENTRIES indexes.
    const keys = Object.keys(map).map(Number).filter(Number.isFinite).sort((a, b) => a - b)
    if (keys.length > MAX_ENTRIES) {
      for (const k of keys.slice(0, keys.length - MAX_ENTRIES)) delete map[k]
    }
    s.setItem(NAV_HISTORY_KEY, JSON.stringify(map))
  } catch { /* private mode / quota — the default target still works */ }
}

/**
 * Candidate "home" targets, in the order Shawn asked for them.  `can` is a
 * (permission) => boolean predicate matching the sidebar's own gate.
 */
export const DEFAULT_BACK_TARGETS = [
  { path: '/calendar', label: 'Calendar', permission: 'calendar' },
  { path: '/matters', label: 'Matters', permission: 'matters' },
  { path: '/dashboard', label: 'Dashboard', permission: null },
]

export function defaultBackTarget (currentPath, can = () => true) {
  const here = (currentPath || '').split(/[?#]/)[0].replace(/\/+$/, '') || '/'
  for (const t of DEFAULT_BACK_TARGETS) {
    if (t.permission && !can(t.permission)) continue
    if (t.path === here) continue
    return t
  }
  return null
}

/**
 * What the Back button should do on `currentPath` at history index `idx`.
 *   { mode: 'history' }                     → navigate(-1)
 *   { mode: 'default', path, label }        → navigate(path)
 *   null                                    → hide the button
 */
export function resolveBack ({ idx, currentPath, history, can } = {}) {
  const here = (currentPath || '').split(/[?#]/)[0].replace(/\/+$/, '') || '/'
  // Dashboard is the home page; Back has never shown there.
  if (here === '/' || here === '/dashboard') return null
  const i = Number.isInteger(idx) ? idx : 0
  if (i > 0) {
    const prev = (history || {})[i - 1]
    if (prev && !isAuthPath(prev)) return { mode: 'history' }
  }
  const t = defaultBackTarget(here, can)
  return t ? { mode: 'default', path: t.path, label: t.label } : null
}
